/**
 * Application Constants
 * All "magic numbers" from the PRD centralized here
 */

// Question Scoring Parameters
export const SCORING = {
  DIFFICULTY_MULTIPLIER: 0.5,        // 50% boost for hardest questions
  HINT_PENALTY_PER_USE: 10,          // 10% penalty per hint
  MAX_HINT_PENALTY: 30,              // Maximum 30% penalty for hints
  CONSISTENCY_THRESHOLD: 3,          // 3 correct in a row
  CONSISTENCY_BONUS: 15,             // +15 points for consistency
  TIME_DECAY_RATE: 0.5,              // 0.5% per hour
  MAX_TIME_DECAY: 20,                // Maximum 20 points time decay
};

// Section Thresholds
export const SECTION = {
  UNLOCK_THRESHOLD: 30,              // Unlock next section at 30%
  LEARNED_THRESHOLD: 85,             // "Learned" badge at 85%
  MASTERED_THRESHOLD: 95,            // "Mastered" badge at 95%
};

// Score Decay Parameters
export const DECAY = {
  BASE_RATE_PER_DAY: 0.02,           // 2% per day
  RECENCY_PROTECTION: 0.4,           // 40% protection for recent sections
  MASTERY_PROTECTION: 0.6,           // 60% protection at 100% mastery
  TRIGGER_THRESHOLD: 5,              // Trigger reinforcement at 5% decay
};

// Ebbinghaus Spaced-Repetition Model (PRD §1.1)
// R = e^(-t / S) where R = retention probability, t = days since last recall, S = memory stability
export const EBBINGHAUS = {
  // Default stability (S) values by successful-recall count.
  // Each entry is how many days before R drops below RETENTION_THRESHOLD.
  // PRD §1.2 default interval progression: 1, 3, 7, 14, 30, 90, 180 days.
  STABILITY_PROGRESSION: [1, 3, 7, 14, 30, 90, 180],
  // Schedule a review when estimated retention drops below this probability (PRD §1.1)
  RETENTION_THRESHOLD: 0.80,
  // On incorrect answer: reduce S by this factor (PRD §1.2 — "reduce stability by 30%")
  STABILITY_FAILURE_FACTOR: 0.70,
};

// Adaptive Question Selection
export const REINFORCEMENT = {
  DECAY_CHANCE: 0.4,                 // 40% chance for decay-triggered
  TIME_CHANCE: 0.2,                  // 20% chance for time-based
  STRUGGLE_CHANCE: 0.3,              // 30% chance for struggle-based
  PREVIEW_CHANCE: 0.1,               // 10% chance for preview
  STRUGGLE_THRESHOLD: 0.5,           // <50% correct rate = struggling
  RECENT_QUESTIONS_COUNT: 5,         // Check last 5 questions
};

// Review Intervals (in minutes for questions, hours for sections)
export const INTERVALS = {
  QUESTION: {
    0: 2,        // 0-30%: 2 minutes
    30: 10,      // 30-50%: 10 minutes
    50: 60,      // 50-70%: 1 hour
    70: 240,     // 70-85%: 4 hours
    85: 1440,    // 85-95%: 1 day (1440 minutes)
    95: 2880,    // 95-100%: 2 days
  },
  SECTION: {
    0: 1,        // 0-30%: 1 hour
    30: 4,       // 30-50%: 4 hours
    50: 24,      // 50-70%: 1 day
    70: 72,      // 70-85%: 3 days
    85: 168,     // 85-95%: 1 week
    95: 336,     // 95-100%: 2 weeks
  },
};

// Question Priority Weights
export const PRIORITY = {
  UNSEEN_QUESTION: 100,              // Massive priority for unseen
  LOW_SCORE_MULTIPLIER: 0.5,         // (100 - score) * 0.5
  DIFFICULTY_MULTIPLIER: 30,         // difficulty * 30
  OVERDUE_BONUS: 50,                 // +50 if overdue
  RECENT_PENALTY_THRESHOLD: 5,       // Minutes before "too soon"
  RECENT_PENALTY_MULTIPLIER: 0.1,    // Severe 90% reduction
};

// Storage Keys
export const STORAGE_KEYS = {
  PROGRESS: 'adaptive_learning_progress',
  COURSES: 'adaptive_learning_courses',
  VERSION: 'adaptive_learning_version',
};

// Data Version
export const DATA_VERSION = '1.0';

// UI Constants
export const UI = {
  SIDEBAR_WIDTH: 280,
  HEADER_HEIGHT: 64,
  MAX_CONTENT_WIDTH: 800,
  ANIMATION_DURATION: 300,           // milliseconds
};

// Application Modes
export const MODES = {
  DEVELOPMENT: 'development',
  PRODUCTION: 'production',
  TEST: 'test',
};

// Question Types (for future expansion)
export const QUESTION_TYPES = {
  MULTIPLE_CHOICE: 'multiple_choice',
  MULTIPLE_SELECT: 'multiple_select',
  TRUE_FALSE: 'true_false',
  FILL_BLANK: 'fill_blank',
  MATCHING: 'matching',
  DRAG_DROP: 'drag_drop',
};

// Section Status
export const SECTION_STATUS = {
  LOCKED: 'locked',
  IN_PROGRESS: 'in_progress',
  LEARNED: 'learned',
  MASTERED: 'mastered',
};

// Question Reason Codes
export const QUESTION_REASON = {
  CURRENT: 'current',
  DECAY: 'decay',
  TIME: 'time',
  STRUGGLE: 'struggle',
  PREVIEW: 'preview',
  RESUME: 'resume',
};
